import React, { useState } from 'react';

import Button from 'components/Button/Button';
import { FormInput } from 'components/FormInput/FormInput';
import FormRow from 'components/FormRow/FormRow';
import { ClientInterface } from 'functions/hooks/useGetUsers';
import { REGEX, validateInput } from 'functions/validationUtils';
import { FormErrors } from 'types/signUpForm';

import { handleOnChange } from './AddClientForm';

import 'layouts/FormLayout/FormLayout.scss';
import { useAppSettings } from 'context/appsettingsContext';
import axios from 'axios';
import { useAuth } from 'context/useAuth';
import { errorAsync, successAsync } from 'functions/notification';
import CheckBox from 'components/CheckBox/CheckBox';
import FormInputSelect, { SelectOptionType } from 'components/FormInputSelect/FormInputSelect';
import { time } from 'console';

interface EditClientProps {
  userDetails: ClientInterface;
  closeModal?: () => any | Promise<any>;
  userUpdated?: (userDetails: ClientInterface) => any | Promise<any>
}

export default function EditClientForm({ userDetails, userUpdated, closeModal }: EditClientProps) {

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { authorisedUser } = useAuth();
  const settings = useAppSettings();
  const isRSARegion = settings.firebaseProjectId.toLowerCase().endsWith('rsa') || settings.firebaseProjectId.toLowerCase().endsWith('rsa-stg');
  const isUSARegion = settings.firebaseProjectId.toLowerCase().endsWith('usa') || settings.firebaseProjectId.toLowerCase().endsWith('usa-stg');
  const isSouthAfricanId = REGEX.IdRSA.test(userDetails.idNumber);
  const [clientDetails, setClientDetails] = useState<ClientInterface>({
    createdOnDateTime: userDetails.createdOnDateTime,
    email: userDetails.email,
    firstName: userDetails.firstName,
    id: userDetails.id,
    idNumber: userDetails.idNumber,
    idNumberIsSouthAfrican: isSouthAfricanId,
    lastName: userDetails.lastName,
    phoneCode: userDetails.phoneCode || (isRSARegion ? '27' : isUSARegion ? '1' : /*isUKRegion*/ '44'),
    phoneNumber: userDetails.phoneNumber,
    tamperActive: userDetails.tamperActive,
    auraDisabled: userDetails.auraDisabled,
    threeDtPackage: userDetails.threeDtPackage,
    notification: {} as any,
  });

  const [clientDetailsError, setClientDetailsError] = useState<FormErrors>({
    Email: [],
    PhoneNumber: [],
    FirstName: [],
    IdNumber: [],
    LastName: [],
  });

  const [threeDtPackages] = useState<SelectOptionType[]>([
    { label: 'None', value: '' },
    { label: 'Lite', value: 'Lite' },
    { label: 'Full', value: 'Full' },
  ]);

  function handleInputValidation(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    setClientDetailsError((prevState) => ({
      ...prevState,
      [id]: validateInput(id, value, isRSARegion, isUSARegion, isSouthAfricanId),
    }));
  }

  async function handleSubmit() {
    
    setIsSubmitting(true);

    try {

      await axios.post<any, any>(settings.updateUserUrl, {
        userId: userDetails.id,
        firstName: clientDetails.firstName,
        lastName: clientDetails.lastName,
        email: clientDetails.email,
        emailVerified: true,
        phoneCode: clientDetails.phoneCode || (isRSARegion ? '27' : isUSARegion ? '1' : /*isUKRegion*/ '44'),
        phoneNumber: clientDetails.phoneNumber,
        idNumber: clientDetails.idNumber,
        idNumberIsSouthAfrican: clientDetails.idNumberIsSouthAfrican,
        auraDisabled: clientDetails.auraDisabled,
        threeDtPackage: clientDetails.threeDtPackage,
      }, { headers: { Authorization: `Bearer ${authorisedUser.accessToken}` }, timeout: 5 * 60 * 1000 });

      successAsync("Saved");

      if (userUpdated) {
        userUpdated(clientDetails);
      }
      
      if (closeModal) {
        closeModal();
      } else {
        window.location.reload();
      }

    } catch (error: any) {
      errorAsync(`Failed to update : ${error?.response?.data?.message || error?.message || error}`, error);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <form className='form__edit'>
        <FormRow>
          <FormInput
            id='firstName'
            label='First Name'
            value={clientDetails.firstName}
            onChange={(e) =>
              handleOnChange(e, setClientDetails, setClientDetailsError)
            }
            errorMessage={clientDetailsError.FirstName}
            onBlur={handleInputValidation}
          />
        </FormRow>
        <FormRow>
          <FormInput
            id='lastName'
            label='Last Name'
            value={clientDetails.lastName}
            onChange={(e) =>
              handleOnChange(e, setClientDetails, setClientDetailsError)
            }
            errorMessage={clientDetailsError.LastName}
            onBlur={handleInputValidation}
          />
        </FormRow>
        <FormRow>
          <FormInput
            id='email'
            label='Email address'
            value={clientDetails.email}
            onChange={(e) =>
              handleOnChange(e, setClientDetails, setClientDetailsError)
            }
            errorMessage={clientDetailsError.Email}
            onBlur={handleInputValidation}
          />
        </FormRow>
        <FormRow>
          <FormInput
            type='tel'
            id='phoneNumber'
            value={clientDetails.phoneNumber}
            label='Cellphone number'
            onChange={(e) =>
              handleOnChange(e, setClientDetails, setClientDetailsError)
            }
            errorMessage={clientDetailsError.PhoneNumber}
            onBlur={handleInputValidation}
            countryCode={isRSARegion ? '+27' : isUSARegion ? '+1' : /*isUKRegion*/ '+44'}
          />
        </FormRow>
        <FormRow>
          <FormInput
            type='text'
            id='idNumber'
            label='ID Number'
            value={clientDetails.idNumber}
            onChange={(e) =>
              handleOnChange(e, setClientDetails, setClientDetailsError)
            }
            errorMessage={clientDetailsError.IdNumber}
            onBlur={handleInputValidation}
            />
        </FormRow>
        {userDetails.threeDtPackage && <FormRow>
            <FormInput
                type='text'
                id='txtThreeDtPackage'
                label='3DT Package'
                disabled={true}
                value={clientDetails.threeDtPackage}
                />
            </FormRow>
        }
        {!userDetails.threeDtPackage && <FormRow>
            <FormInputSelect
                label='3DT Package'
                options={threeDtPackages}
                value={threeDtPackages.find(x => x.value === (clientDetails.threeDtPackage || ''))!}
                handleSelection={(e) => setClientDetails((prevState: any) => ({
                    ...prevState,
                    threeDtPackage: e.value,
                }))}
                />
            </FormRow>
        }
        <FormRow>
          <CheckBox
            id='auraDisabled'
            name='auraDisabled'
            label='Disable AURA'
            checked={clientDetails.auraDisabled}
            onChange={(e) =>
              handleOnChange(e, setClientDetails, setClientDetailsError)
            }
            />
        </FormRow>
      </form>

      <div className='form-actions'>
        <Button
          label={isSubmitting ? 'Submitting' : 'Save'}
          small
          onClick={handleSubmit}
          disabled={
            !clientDetails.phoneNumber || !clientDetails.email || isSubmitting
          }
        />
      </div>
    </>
  );
}